import { useCallback, useEffect, useRef } from 'react';

import { useAuthStore } from '~/stores/use-auth/store/authStore';
import {
  TOKEN_ALLOWANCE_FEATURE_FLAG,
  useFlags,
} from '~/stores/use-flags/useFlags';
import { useTokenUsage } from '~/stores/use-token-usage/useTokenUsage';

const MAX_BROWSER_TIMEOUT_MS = 2_147_483_647;
const RESET_REFRESH_BUFFER_MS = 1_000;
const RESET_REFRESH_RETRY_DELAY_MS = 30_000;

const shouldRetryResetRefresh = () => {
  const { status, unavailableReason } = useTokenUsage.getState();

  return status === 'exhausted' && unavailableReason === undefined;
};

export const useTokenUsageRecovery = () => {
  const userId = useAuthStore(state => state.user_id);
  const isFeatureEnabled = useFlags(state =>
    state.getFeatureFlagState(TOKEN_ALLOWANCE_FEATURE_FLAG)
  );
  const { fetchUsage, resetAt, status } = useTokenUsage(state => ({
    fetchUsage: state.fetchUsage,
    resetAt: state.usage?.reset_at,
    status: state.status,
  }));
  const resetTimeoutRef = useRef<number | null>(null);

  const clearResetTimeout = useCallback(() => {
    if (resetTimeoutRef.current !== null) {
      window.clearTimeout(resetTimeoutRef.current);
      resetTimeoutRef.current = null;
    }
  }, []);

  useEffect(() => {
    clearResetTimeout();

    if (!isFeatureEnabled || !userId || status !== 'exhausted' || !resetAt) {
      return undefined;
    }

    const resetTime = Date.parse(resetAt) + RESET_REFRESH_BUFFER_MS;

    if (!Number.isFinite(resetTime)) {
      return undefined;
    }

    let isCancelled = false;

    const refreshAfterReset = async () => {
      await fetchUsage('reconnect');

      if (isCancelled || !shouldRetryResetRefresh()) {
        return;
      }

      resetTimeoutRef.current = window.setTimeout(() => {
        resetTimeoutRef.current = null;

        if (shouldRetryResetRefresh()) {
          useTokenUsage.getState().fetchUsage('reconnect');
        }
      }, RESET_REFRESH_RETRY_DELAY_MS);
    };

    if (resetTime <= Date.now()) {
      refreshAfterReset();
      return () => {
        isCancelled = true;
        clearResetTimeout();
      };
    }

    const scheduleResetRefresh = () => {
      const remainingDelay = resetTime - Date.now();

      if (remainingDelay <= 0) {
        resetTimeoutRef.current = null;
        refreshAfterReset();
        return;
      }

      resetTimeoutRef.current = window.setTimeout(
        scheduleResetRefresh,
        Math.min(remainingDelay, MAX_BROWSER_TIMEOUT_MS)
      );
    };

    scheduleResetRefresh();

    return () => {
      isCancelled = true;
      clearResetTimeout();
    };
  }, [
    clearResetTimeout,
    fetchUsage,
    isFeatureEnabled,
    resetAt,
    status,
    userId,
  ]);

  useEffect(() => {
    if (!isFeatureEnabled || !userId) {
      return undefined;
    }

    const handleFocus = () => {
      const tokenUsageState = useTokenUsage.getState();

      if (tokenUsageState.status === 'exhausted') {
        tokenUsageState.fetchUsage('reconnect');
      }
    };

    window.addEventListener('focus', handleFocus);

    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [isFeatureEnabled, userId]);
};
